import React, { useMemo, useState, useEffect } from "react";
import useApi from "../../CustomHooks/useApi";
import { financeAPI } from "../../../api/apiClient";
import ReservationStatusEnum from "../../../Enums/ReservationStatusEnum";
import "../../../css/Finance/FullRefundReservationsSection.css";

const groupReservationsByVariant = (reservations) => {
  return reservations.reduce((acc, reservation) => {
    const key = reservation.variantGuid;

    if (!acc[key]) {
      acc[key] = {
        variantGuid: reservation.variantGuid,
        variantName: reservation.variantName || "Bez wariantu",
        reservations: [],
      };
    }

    acc[key].reservations.push(reservation);
    return acc;
  }, {});
};

const AddFullRefundReservationPopup = ({
  isOpen,
  availableReservations,
  onClose,
  onAdd,
  savingReservationGuid,
  isLoading,
}) => {
  const [search, setSearch] = useState("");
  useEffect(() => {
  if (!isOpen) return;

  const originalBodyOverflow = document.body.style.overflow;
  const originalHtmlOverflow = document.documentElement.style.overflow;

  document.body.style.overflow = "hidden";
  document.documentElement.style.overflow = "hidden";

  return () => {
    document.body.style.overflow = originalBodyOverflow;
    document.documentElement.style.overflow = originalHtmlOverflow;
  };
}, [isOpen]);
  const filteredReservations = useMemo(() => {
    const phrase = search.trim().toLowerCase();
    if (!phrase) return availableReservations;

    return availableReservations.filter((reservation) => {
      const haystack = [
        reservation.reservationCode,
        reservation.primaryParticipantName,
        reservation.variantName,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(phrase);
    });
  }, [availableReservations, search]);

  const groupedReservations = useMemo(() => {
    const grouped = Object.values(groupReservationsByVariant(filteredReservations));

    return grouped.sort((a, b) =>
      (a.variantName || "").localeCompare(b.variantName || "", "pl", {
        sensitivity: "base",
      })
    );
  }, [filteredReservations]);

  if (!isOpen) return null;

  return (
    <div className="full-refund-popup-overlay" onClick={onClose}>
      <div className="full-refund-popup" onClick={(e) => e.stopPropagation()}>
        <div className="full-refund-popup-header">
          <div>
            <div className="full-refund-popup-title">
              Dodaj rezerwację do pełnego zwrotu
            </div>
            <div className="full-refund-popup-subtitle">
              Wybierz anulowaną rezerwację. Popup pozostaje otwarty, aby można było dodać kilka pozycji.
            </div>
          </div>

          <div className="full-refund-popup-close" onClick={onClose}>
            <i className="fa-solid fa-xmark" />
          </div>
        </div>

        <div className="full-refund-popup-search">
          <i className="fa-solid fa-magnifying-glass" />
          <input
            type="text"
            placeholder="Szukaj po kodzie, osobie lub wariancie"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="full-refund-popup-list">
          {groupedReservations.length === 0 ? (
            <div className="draft-empty">
              Brak anulowanych rezerwacji do dodania.
            </div>
          ) : (
            groupedReservations.map((group) => (
              <div key={group.variantGuid || group.variantName} className="full-refund-popup-group">
                <div className="full-refund-popup-group-title">
                  {group.variantName}
                </div>

                <div className="full-refund-popup-group-list">
                  {group.reservations.map((reservation) => {
                    const isSaving =
                      savingReservationGuid === reservation.reservationGuid;

                    return (
                      <div
                        key={reservation.reservationGuid}
                        className={`full-refund-popup-row ${isSaving ? "is-saving" : ""}`}
                        onClick={() => {
                          if (!isSaving && !isLoading) {
                            onAdd(reservation);
                          }
                        }}
                      >
                        <div className="full-refund-popup-row-left">
                          <span className={`reg-code status-${reservation.status}`}>
                            {reservation.reservationCode}
                          </span>

                          <div className="full-refund-popup-row-name">
                            {reservation.primaryParticipantName || "—"}
                          </div>
                        </div>

                        {reservation.hasExcludedNnwFromFullRefund && reservation.excludedNnwMessage && (
                          <div className="full-refund-warning-note">
                            {reservation.excludedNnwMessage}
                          </div>
                        )}

                        <div className="full-refund-popup-row-right">
                          {isSaving ? (
                            <i className="fa fa-spinner fa-spin" />
                          ) : (
                            <i className="fa-solid fa-plus" />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

const FullRefundReservationsSection = ({
  tripGuid,
  draft,
  groupedReservations,
  onDraftChanged,
  showToast,
  clearToast,
  canEdit
}) => {
  const { callApi, isLoading } = useApi();
  const [savingReservationGuid, setSavingReservationGuid] = useState(null);
  const [showAddPopup, setShowAddPopup] = useState(false);
  

  const fullRefundMap = useMemo(() => {
    const map = {};
    (draft?.reservations ?? []).forEach((r) => {
        map[r.reservationGuid] = r;
    });
    return map;
  }, [draft]);

  const cancelledReservations = useMemo(() => {
    return Object.values(groupedReservations)
      .flatMap((group) =>
        (group.reservations ?? [])
          .filter(
            (reservation) =>
              reservation.status === ReservationStatusEnum.CANCELLED
          )
          .map((reservation) => ({
            ...reservation,
            variantName: group.variantName,
          }))
      )
      .sort((a, b) =>
        (a.primaryParticipantName || "").localeCompare(
          b.primaryParticipantName || "",
          "pl",
          { sensitivity: "base" }
        )
      );
  }, [groupedReservations]);

  const selectedReservations = useMemo(() => {
    return cancelledReservations.filter((r) => !!fullRefundMap[r.reservationGuid]);
  }, [cancelledReservations, fullRefundMap]);

  const availableReservations = useMemo(() => {
    return cancelledReservations.filter((r) => !fullRefundMap[r.reservationGuid]);
  }, [cancelledReservations, fullRefundMap]);

  const selectedGrouped = useMemo(() => {
    const grouped = Object.values(groupReservationsByVariant(selectedReservations));

    return grouped.sort((a, b) =>
      (a.variantName || "").localeCompare(b.variantName || "", "pl", {
        sensitivity: "base",
      })
    );
  }, [selectedReservations]);

  const handleToggleFullRefund = (reservation, checked) => {
    if (savingReservationGuid || isLoading) return;

    setSavingReservationGuid(reservation.reservationGuid);

    callApi(
      () =>
        financeAPI.setDraftFullRefund(tripGuid, {
          reservationGuid: reservation.reservationGuid,
          isFullRefund: checked,
          fullRefundReason: checked
            ? "Ustawione ręcznie przez osobę finansową"
            : null,
          notes: null,
        }),
      async () => {
        await onDraftChanged();
        setSavingReservationGuid(null);

        showToast(
          checked
            ? `Dodano pełny zwrot dla rezerwacji ${reservation.reservationCode}`
            : `Usunięto pełny zwrot dla rezerwacji ${reservation.reservationCode}`,
          "success"
        );
      },
      () => {
        clearToast();
        setSavingReservationGuid(null);
      }
    );
  };

  return (
    <div className="draft-card full-refund-picker-card">
      <div className="draft-card-title">1. Rezerwacje do pełnego zwrotu</div>
      {canEdit && <>
      <div className="draft-card-subtitle">
        Wybierz anulowane rezerwacje, które mają zostać objęte pełnym zwrotem.
      </div>

      <div className="full-refund-picker-toolbar">
        <div
          className={`full-refund-add-btn ${
            availableReservations.length === 0 ? "disabled" : ""
          }`}
          onClick={() => {
            if (availableReservations.length > 0) {
              setShowAddPopup(true);
            }
          }}
        >
          <i className="fa-solid fa-plus" />
          <span>Dodaj rezerwację</span>
        </div> 

        <div className="full-refund-picker-count">
          Wybrane: <strong>{selectedReservations.length}</strong>
        </div>
      </div>
      </>
      }

      <div className="full-refund-selected-list">
        {selectedGrouped.length === 0 ? (
          <div className="draft-empty">
            Nie wybrano jeszcze żadnej rezerwacji do pełnego zwrotu.
          </div>
        ) : (
          selectedGrouped.map((group) => (
            <div
              key={group.variantGuid || group.variantName}
              className="full-refund-selected-group"
            >
              <div className="full-refund-selected-group-title">
                {group.variantName}
              </div>

              <div className="full-refund-selected-group-list">
                {group.reservations.map((reservation) => {
                  const isSaving =
                    savingReservationGuid === reservation.reservationGuid;

                  return (
                    <div
                      key={reservation.reservationGuid}
                      className={`full-refund-selected-row ${
                        isSaving ? "is-saving" : ""
                      }`}
                    >
                    <div className="full-refund-selected-row-left">
                      <span className={`reg-code status-${reservation.status}`}>
                        {reservation.reservationCode}
                      </span>

                      <div className="full-refund-selected-row-text">
                        <div className="full-refund-selected-row-name">
                          {reservation.primaryParticipantName || "—"}
                        </div>

                        {(reservation.participants ?? [])
                          .filter(
                            (p) =>
                              (p.name || "").trim() &&
                              (p.name || "").trim() !== (reservation.primaryParticipantName || "").trim()
                          )
                          .length > 0 && (
                          <div className="full-refund-selected-row-companions">
                            <span className="full-refund-companions-prefix">+</span>

                            {(reservation.participants ?? [])
                              .filter(
                                (p) =>
                                  (p.name || "").trim() &&
                                  (p.name || "").trim() !== (reservation.primaryParticipantName || "").trim()
                              )
                              .map((p, idx) => (
                                <span key={p.participantGuid || p.guid || idx}>
                                  {p.name}
                                </span>
                              ))
                              .reduce((acc, el, idx) => {
                                if (idx === 0) return [el];
                                return [...acc, ", ", el];
                              }, [])}
                          </div>
                        )}

                        {reservation.hasExcludedNnwFromFullRefund && reservation.excludedNnwMessage && (
                          <div className="full-refund-warning-note">
                            {reservation.excludedNnwMessage}
                          </div>
                        )}
                      </div>
                    </div>
                      { canEdit && <div
                          className="full-refund-remove-icon"
                          onClick={() => {
                            if (!isSaving && !isLoading) {
                              handleToggleFullRefund(reservation, false);
                            }
                          }}
                        >
                          {isSaving ? (
                            <i className="fa fa-spinner fa-spin" />
                          ) : (
                            <i className="fa-solid fa-xmark" />
                          )}
                        </div>
                      }
                  </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>

      {canEdit && <AddFullRefundReservationPopup
        isOpen={showAddPopup}
        availableReservations={availableReservations}
        savingReservationGuid={savingReservationGuid}
        isLoading={isLoading}
        onClose={() => {
          if (!savingReservationGuid) {
            setShowAddPopup(false);
          }
        }}
        onAdd={(reservation) => handleToggleFullRefund(reservation, true)}
      />
      }
    </div>
  );
};

export default FullRefundReservationsSection;